// Added by Sejune Oh on 2019-03-19
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { Component } from 'react'
import styled from 'styled-components'
import { TableBodyProps } from '@material-ui/core/TableBody'
import { TableRowProps } from '@material-ui/core/TableRow'
import { TableCellProps } from '@material-ui/core/TableCell'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { ReactClickEvent } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime Elements
import { TableCell, TableRow, Checkbox, TableBody as PTableBody } from '@/components/_primes'

// Components
import TableBody, { TableBodyBaseFCProps } from './TableBody'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface TableBodyFCProps extends TableBodyBaseFCProps {
    onSelect: (value: number) => void
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
class TableBodyContainer extends Component<TableBodyFCProps> {
    render() {
        const { ...others } = this.props

        return <TableBody {...others} rowElements={this._getRows()} />
    }

    handleSelectClick: ReactClickEvent = (event) => {
        event.stopPropagation()
        const index = parseInt(event.currentTarget.dataset.index as string)
        this.props.onSelect(index)
    }

    _getRows = () => {
        const { rows, headerProps } = this.props
        let headerKeys: string[][] = []
        let types: Array<string> = []
        headerProps.forEach(({ key, disp_key, type = 'string' }) => {
            headerKeys.push([key!, disp_key!])
            types.push(type)
        })
        let rtnTableRows: Array<JSX.Element> = []
        let rowSpanSize: number | null
        let colIndex: number | null

        rows.map((row, rowIndex) => {
            ;[colIndex, rowSpanSize] = this._getMultiColSpec(headerKeys, types, row)
            rtnTableRows.push((colIndex === null
                ? this._getNormalRows(rowIndex, headerKeys, types, row)
                : this._getExpandedRowSpanRows(
                      rowIndex,
                      headerKeys,
                      types,
                      row,
                      colIndex,
                      rowSpanSize!
                  )) as JSX.Element)
            return rowIndex
        })

        return rtnTableRows
    }

    _getMultiColSpec = (headerKeys: string[][], types: Array<string>, row: any) => {
        let curKey = ''
        for (let keyIndex = 0, max = headerKeys.length; keyIndex < max; keyIndex++) {
            curKey = headerKeys[keyIndex][1]
            if (typeof row[curKey] === 'object' && types[keyIndex] !== 'jsx') return [keyIndex, row[curKey].length]
        }

        return [null, null]
    }

    _getFirstCell = (index: number, nonenumerable: boolean, rowSpanSize?: number): JSX.Element => {
        const { selectedIndexArr, isSelectable } = this.props
        const rowSpan = rowSpanSize ? { rowSpan: rowSpanSize } : {}

        return isSelectable ? (
            <CustomCheckBoxTableCell padding="checkbox" key={`t-fc-${index}`} {...rowSpan}>
                {!nonenumerable ? (
                    <Checkbox
                        style={{ width: 45 }}
                        data-index={index}
                        checked={selectedIndexArr.has(index)}
                        onClick={this.handleSelectClick}
                        color={'primary'}
                    />
                ) : (
                    <CustomUnselectableTableCellWrapper>
                        <CustomUnselectableTableCell />
                    </CustomUnselectableTableCellWrapper>
                )}
            </CustomCheckBoxTableCell>
        ) : (
            <TableCell align="left" key={`t-fc-${index}`} {...rowSpan} style={{ width: 12, padding: 0 }} />
        )
    }

    _getExpandedRowSpanRows = (
        rowIndex: number,
        headerKeys: string[][],
        colTypes: Array<string>,
        row: any,
        colIndex: number,
        rowSpanSize: number
    ) => {
        const { customColSpan, isRowSelectable, onTableRowClick } = this.props
        const rowSelectable = typeof isRowSelectable === 'undefined' ? true : isRowSelectable
        let rows: Array<JSX.Element> = []
        let multiRows: Array<JSX.Element> = []

        rows.push(this._getFirstCell(rowIndex, row.nonenumerable, rowSpanSize))

        headerKeys.map(([key, dispKey], index) => {
            const multiRowFlag = index === colIndex
            const isJsxColumn = colTypes[index] === 'jsx'
            const title = isJsxColumn ? row[`${key}`] : row[`${dispKey}`]
            let rowSpanOption: { [key: string]: any } = { rowSpan: rowSpanSize }
            let tempValue = row[`${dispKey}`]

            if (multiRowFlag) {
                rowSpanOption = {}
                for (
                    let multiRowIndex = 1, maxRowCount = tempValue.length;
                    multiRowIndex < maxRowCount;
                    multiRowIndex++
                ) {
                    rowSpanOption['data-cy-col'] = tempValue[multiRowIndex]
                    multiRows.push(
                        <CustomTableRow key={`t-mr-${multiRowIndex}-${rowIndex}`}>
                            {isJsxColumn ? (
                                <CustomTableJSXCell
                                    align="left"
                                    key={`t-c-${index}`}
                                    title={tempValue[multiRowIndex]}
                                    colSpan={customColSpan ? customColSpan[index] : 2}
                                    {...rowSpanOption}
                                >
                                    {tempValue[multiRowIndex]}
                                </CustomTableJSXCell>
                            ) : (
                                <CustomTableCell
                                    align="left"
                                    key={`t-c-${index}`}
                                    title={tempValue[multiRowIndex]}
                                    colSpan={customColSpan ? customColSpan[index] : 2}
                                    {...rowSpanOption}
                                >
                                    {tempValue[multiRowIndex]}
                                </CustomTableCell>
                            )}
                        </CustomTableRow>
                    )
                }
                tempValue = tempValue[0]
            }

            rows.push(
                isJsxColumn ? (
                    <CustomTableJSXCell
                        align="left"
                        key={`t-c-${index}`}
                        colSpan={customColSpan ? customColSpan[index] : 2}
                        title={title}
                        {...rowSpanOption}
                    >
                        {tempValue}
                    </CustomTableJSXCell>
                ) : (
                    <CustomTableCell
                        align="left"
                        key={`t-c-${index}`}
                        colSpan={customColSpan ? customColSpan[index] : 2}
                        title={title}
                        {...rowSpanOption}
                    >
                        {tempValue}
                    </CustomTableCell>
                )
            )

            return index
        })

        return rowSelectable ? (
            <CustomTableBody key={`t-b-${rowIndex}`} data-id={row.id} onClick={onTableRowClick}>
                <CustomTableRow key={`t-r-${rowIndex}`}>{rows}</CustomTableRow>
                {multiRows}
            </CustomTableBody>
        ) : (
            <PTableBody key={`t-b-${rowIndex}`}>
                <CustomTableRow key={`t-r-${rowIndex}`}>{rows}</CustomTableRow>
                {multiRows}
            </PTableBody>
        )
    }

    _getNormalRows = (rowIndex: number, headerKeys: string[][], colTypes: Array<string>, row: any) => {
        const { customColSpan, isRowSelectable, onTableRowClick } = this.props
        const rowSelectable = typeof isRowSelectable === 'undefined' ? true : isRowSelectable
        const commonOption = {
            key: `t-r-${rowIndex}`,
            hover: typeof isRowSelectable === 'undefined' ? true : isRowSelectable,
        }
        let rows: Array<JSX.Element> = []

        rows.push(this._getFirstCell(rowIndex, row.nonenumerable))

        headerKeys.map(([key, dispKey], index) => {
            const isJsxColumn = colTypes[index] === 'jsx'
            const title = isJsxColumn ? row[`${key}`] : row[`${dispKey}`]
            const column = row[`${dispKey}`]
            const commonProps: TableCellProps = {
                align:"left",
                colSpan: customColSpan ? customColSpan[index] : 2,
                title: typeof title === 'object' ? undefined : title
            }

            rows.push(
                isJsxColumn ? (
                    <CustomTableJSXCell
                    key={`t-c-${index}`}
                    data-cy-col={title}
                    data-testid={title}
                    {...commonProps}
                    >
                        {column}
                    </CustomTableJSXCell>
                ) : (
                    <CustomTableCell
                    key={`t-c-${index}`}
                    data-cy-col={title}
                    data-testid={title}
                    {...commonProps}
                    >
                        {column}
                    </CustomTableCell>
                )
            )

            return index
        })

        return (
            <PTableBody key={`t-b-${rowIndex}`}>
                {rowSelectable ? (
                    <CustomSelectableTableRow {...commonOption} data-id={row.id} onClick={onTableRowClick}>
                        {rows}
                    </CustomSelectableTableRow>
                ) : (
                    <CustomTableRow aria-disabled={!this.props.isRowSelectable} {...commonOption}>{rows}</CustomTableRow>
                )}
            </PTableBody>
        )
    }
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const CustomTableBody = styled(PTableBody as React.FC<TableBodyProps>)`
    && {
        > tr {
            cursor: pointer;
        }

        &:hover {
            background-color: rgba(255, 255, 255, 0.14);
        }
    }
`

// tableRow
const CustomTableRow = styled(TableRow as React.FC<TableRowProps>)`
    && {
        cursor: default;

        > td {
            ${(props) => props['aria-disabled'] && 'color: rgba(255, 255, 255, 0.3) !important;'}
        }
    }
`

// tableRowUnselectable
const CustomSelectableTableRow = styled(TableRow as React.FC<TableRowProps>)`
    && {
        cursor: pointer;
    }
`

// tableCell
const CustomTableCell = styled(TableCell as React.FC<TableCellProps>)`
    && {
        position: relative;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        padding-left: 12px;
        padding-right: 12px;
    }
`
const CustomTableJSXCell = styled(TableCell as React.FC<TableCellProps>)`
    && {
        position: relative;
        padding-left: 12px;
        padding-right: 12px;
    }
`
const CustomCheckBoxTableCell = styled(TableCell as React.FC<TableCellProps>)`
    && {
        width: 24;
        padding: 0;
        position: relative;
        & > span:first-of-type {
            justify-content: flex-start;

            & > input {
                position: absolute;
                left: 0;
                top: 0;
                bottom: 0;
                right: 0;
                margin: auto;
            }
        }
    }
`

// unselectableCellWrapper
const CustomUnselectableTableCellWrapper = styled.span`
    && {
        position: 'absolute';
        width: 48px;
        height: 48px;
        right: 0;
        top: 0;
        bottom: 0;
        left: 12px;
        display: 'flex';
        justify-content: 'center';
        align-items: 'center';
    }
`

// unselectableCell
const CustomUnselectableTableCell = styled.span`
    && {
        width: 1.4em;
        height: 1.4em;
        background-color: #999;
        border-radius: 3px;
    }
`

export default TableBodyContainer
